// src/pages/HowItWorks.js

import React from 'react';
import { Link } from 'react-router-dom';
import { UserPlus, Search, Calendar, Star, Video } from 'lucide-react';
import { motion } from 'framer-motion';
import '../components/css/HowItWorks.css';

// Import the new images
import profileImage from '../assets/img/profile.png';
import browseImage from '../assets/img/browse.png';
import scheduleImage from '../assets/img/schedule.png';
import performImage from '../assets/img/perform.png';
import shortlistedImage from '../assets/img/shortlisted.png';

const steps = [
  {
    stepNumber: '01',
    title: 'Create Your Profile',
    description: 'Sign up as a Talent or Casting Pro. Build a standout portfolio with your media and skills to get noticed.',
    icon: <UserPlus size={22} />,
    image: profileImage,
  },
  {
    stepNumber: '02',
    title: 'Browse & Apply',
    description: 'Search through thousands of verified roles. Apply instantly with one click or submit a custom self-tape.',
    icon: <Search size={22} />,
    image: browseImage,
  },
  {
    stepNumber: '03',
    title: 'Schedule Audition',
    description: 'Book a virtual slot via Google Meet or get venue details for an in-person audition, all on one platform.',
    icon: <Calendar size={22} />,
    image: scheduleImage,
  },
  {
    stepNumber: '04',
    title: 'Perform & Feedback',
    description: 'Impress in your live meet or uploaded tape. Get instant AI-powered scoring and direct feedback.',
    icon: <Video size={22} />,
    image: performImage,
  },
  {
    stepNumber: '05',
    title: 'Get Shortlisted',
    description: 'Track your application status in real-time. Receive instant notifications the moment you’re hired.',
    icon: <Star size={22} />,
    image: shortlistedImage,
  },
];

// Helper Components for the Alternating Layout
const ImageBlock = ({ step }) => (
  <div className="hit-block hit-image-block">
    <div className="hit-step-visual-card">
      <img src={step.image} alt={step.title} className="hit-step-visual-img" />
    </div>
  </div>
);

const TextBlock = ({ step }) => (
  <div className="hit-block hit-text-block">
    <span className="hit-step-bg-number">{step.stepNumber}</span>
    <h3 className="hit-step-title">{step.title}</h3>
    <p className="hit-step-description">{step.description}</p>
  </div>
);

const HowItWorks = () => {
  return (
    <div className="how-works-page">
      <section className="hit-section">
        <div className="hit-container-fluid">
          
          <div className="hit-header">
            <span className="hit-badge">PROCESS</span>
            <h1 className="hit-title">
              How It <span>Works</span>
            </h1>
            <p className="hit-subtitle">
              From profile creation to getting hired, we've streamlined every step of the audition process for a seamless experience.
            </p>
          </div>

          {/* --- HORIZONTAL TIMELINE (DESKTOP) / VERTICAL (MOBILE) --- */}
          <div className="hit-timeline-wrapper">
            {/* The horizontal connecting line (hidden on mobile) */}
            <div className="hit-horizontal-line d-none d-lg-block" />
            
            {/* The vertical connecting line (hidden on desktop) */}
            <div className="hit-vertical-line d-lg-none" />

            <div className="hit-steps-grid">
              {steps.map((step, i) => {
                const isEven = i % 2 === 0; // 0, 2, 4 (Steps 1, 3, 5)

                return (
                  <motion.div
                    key={step.stepNumber}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.5, delay: i * 0.15 }}
                    className="hit-step-column"
                  >
                    {/* Desktop: Alternating Layout. Mobile: Standard Stack */}
                    <div className={`hit-step-content-wrapper ${isEven ? 'layout-even' : 'layout-odd'}`}>
                      
                      {/* Top Section (Desktop) / First Section (Mobile) */}
                      <div className="hit-cell hit-cell-top">
                        <div className="d-none d-lg-block">
                           {isEven ? <ImageBlock step={step} /> : <TextBlock step={step} />}
                        </div>
                        {/* On mobile, ALWAYS show text first */}
                        <div className="d-block d-lg-none">
                           <TextBlock step={step} />
                        </div>
                      </div>

                      {/* Middle Section: Icon on the Track */}
                      <div className="hit-cell hit-cell-middle">
                        <div className="hit-timeline-icon">{step.icon}</div>
                      </div>

                      {/* Bottom Section (Desktop) / Last Section (Mobile) */}
                      <div className="hit-cell hit-cell-bottom">
                        <div className="d-none d-lg-block">
                          {isEven ? <TextBlock step={step} /> : <ImageBlock step={step} />}
                        </div>
                        {/* On mobile, ALWAYS show image last */}
                        <div className="d-block d-lg-none mt-3">
                           <ImageBlock step={step} />
                        </div>
                      </div>

                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* CTA SECTION */}
          <div className="hit-cta-wrap">
            <div className="hit-cta-card">
              <h2 className="hit-cta-title">Ready to Launch Your Career?</h2>
              <p className="hit-cta-subtitle">Join thousands of artists and casting professionals on the industry's leading platform.</p>
              <div className="hit-cta-actions">
                <Link to="/register" className="hit-btn hit-btn-primary">
                  Join for Free
                </Link>
                <Link to="/roles" className="hit-btn hit-btn-outline">
                  Browse Roles
                </Link>
              </div>
            </div>
          </div>

        </div>
      </section>
    </div>
  );
};

export default HowItWorks;